/**
 * CC-1b — audit-persist wire contract (ТЗ §14 audit: clicks/commands/params/
 * errors/timings).
 *
 * # Why this exists
 *
 * The admin AUDIT console (CC-1d, `GET /api/v1/admin/audit`) reads the Laravel
 * `audit_events` table, but nothing WRITES it: `AuditEvent::create` is called
 * nowhere but the seeder, so the console renders only the 3 seeded rows. This
 * module is the single source of truth for the browser→backend flush that
 * finally persists the in-memory audit ring into `audit_events`.
 *
 * It is the PRODUCER contract (mirrors `telemetryBatchSchema`): the web client
 * always emits the CURRENT `AUDIT_SCHEMA_VERSION`, so the literal tracks the
 * constant and never widens to a union. The consumer
 * (`App\Http\Controllers\Api\AuditIngestController`) validates an inbound batch
 * against the JSON Schema GENERATED from this Zod contract (via
 * `npm run contracts:generate` → `apps/api/resources/contracts/index.json`,
 * loaded by `App\Support\ContractSchema`), NOT a hand-maintained parallel set
 * of PHP validation rules — the API-CONTRACT-CODEGEN gate (#315/#316) this
 * slice is unblocked by.
 *
 * # Why a SEPARATE contract from telemetry
 *
 * `telemetry_events` (CC-1a) is the aggregate-analytics table (counts by kind /
 * status / command_type). `audit_events` is the richer PER-ACTION audit log the
 * admin console filters by user/command/source/space/date and whose detail
 * panel surfaces the command, its alias, the affected entity ids and the
 * before/after summary. The audit RECORD therefore carries the audit-table
 * columns, not the telemetry event shape — a distinct wire contract.
 *
 * The record deliberately carries NO `workspace_id` / `drawing_id` / `user_id` /
 * `user_email` / `user_role`: authoritative identity + tenancy come from the
 * SESSION on the server, never the payload (mirrors the telemetry ingest's
 * "identity from the session, not the payload actorEmail" invariant).
 */

import { z } from "zod";

import { commandSourceSchema } from "./editor";

/**
 * Bump on any breaking wire-shape change so the ingest can version-gate / reject
 * old payloads (mirrors `TELEMETRY_SCHEMA_VERSION`). v1 is the initial CC-1b
 * shape.
 */
export const AUDIT_SCHEMA_VERSION = 1 as const;

/**
 * Resolved outcome persisted to `audit_events.status`. Aligned with the CC-1d
 * admin console's status-pill CSS classes (`is-success` / `is-warning` /
 * `is-error`, `styles.css`) and the seeder's `success` / `warning` convention.
 * The browser projection maps the audit-ring `tone`
 * (`info`/`success`/`warning`/`error`) → this domain (`info`+`success` →
 * `success`).
 */
export const auditPersistStatusSchema = z.enum(["success", "warning", "error"]);

/**
 * The `audit_events` VARCHAR column width (Laravel `->string()` default, 255).
 *
 * Every payload-supplied field that lands in one of those `varchar(255)` columns
 * MUST be bounded here so an over-long value is rejected AT THE CONTRACT — a 422
 * (`validation-drop`, terminal) on the server / a pre-flight `validateEvent`
 * reject on the client — instead of a raw INSERT overflow. An unbounded string
 * that a strict PG/MySQL rejects at insert returns 500, and the batched channel
 * treats a 5xx as TRANSIENT (`batchedServerChannel.sendWithRetry`): the chunk is
 * retried forever and head-of-line-blocks EVERY later audit event. Bounding here
 * converts that unbounded non-retryable failure into a deterministic 422 the
 * channel drops-and-advances (or a client-side reject that never leaves the
 * queue). Columns backed by `text` (before/after summary) or a bounded enum
 * (`commandSource` / `status`) need no bound and are excluded.
 *
 * Exported so the browser audit projection truncates the human `command` summary
 * to the SAME width (one source of truth for the column bound), producing a
 * schema-valid, insert-safe record instead of dropping a long-summary event.
 */
export const AUDIT_VARCHAR_MAX = 255;

/**
 * Upper bound on the number of affected-entity ids persisted per audit record.
 *
 * `affectedEntityIdsForCommand` stamps the WHOLE affected-id list on a
 * multi-entity command (a SELECT-ALL + CHPROP / properties-apply over a large
 * drawing can be tens of thousands of ids). `auditBatchSchema` caps the event
 * COUNT (`.max(500)`) but nothing bounded this per-record list, so one
 * pathological record could inflate the batch body past the web server / Laravel
 * request-size limit. That yields a deterministic oversized-payload rejection
 * (e.g. 413) which — before the channel's terminal 413 handling — the batched
 * channel retried forever, head-of-line-blocking every later audit event.
 *
 * Bounding the list here (with the projection TRUNCATING a longer list to fit,
 * mirroring the `command` varchar clamp) keeps the record schema-valid,
 * insert-safe, and small enough to deliver — preserving the command/status/
 * timing evidence plus the first `AUDIT_ENTITY_IDS_MAX` affected ids — instead of
 * dropping the whole record. 5000 is far beyond any human-reviewable affected-set
 * while keeping a single record's id list well under the request-size budget.
 */
export const AUDIT_ENTITY_IDS_MAX = 5000;

/**
 * ONE persisted audit record — the wire projection of an in-memory audit-ring
 * entry. Every field maps to an `audit_events` column the client can supply;
 * the server layers on the session-authoritative identity/tenancy columns.
 */
export const auditRecordSchema = z.object({
  schemaVersion: z.literal(AUDIT_SCHEMA_VERSION),
  /**
   * Collision-resistant client-minted dedupe key (mirrors telemetry's
   * `clientEventUid`). The ingest keys `firstOrCreate` on
   * `(user_id, client_event_uid)`, so a lost-response channel retry re-POSTs
   * the identical uid and is a no-op instead of double-inserting an audit row.
   * `client_event_uid` is a `varchar(255)` column → bounded (a minted UUID is
   * ~36 chars, so this only guards a malformed/oversized injected value).
   */
  clientEventUid: z.string().min(1).max(AUDIT_VARCHAR_MAX),
  /**
   * Human-readable command description (the audit-ring `summary`) — what the
   * console's Command column renders and its command filter matches. `command`
   * is a `varchar(255)` column, so the wire value is bounded; the projection
   * TRUNCATES a longer summary to fit (never drops the audit event over a long
   * error-message summary).
   */
  command: z.string().min(1).max(AUDIT_VARCHAR_MAX),
  /**
   * Machine command key (the audit-ring `commandType` / view-op `opType` / ui
   * `op`) — the alias arm the console's command filter ALSO matches on. Backed
   * by a `varchar(255)` column → bounded.
   */
  alias: z.string().min(1).max(AUDIT_VARCHAR_MAX),
  /**
   * Bound hotkey, when the command was driven by one; `null` otherwise. Backed
   * by a `varchar(255)` column → bounded.
   */
  hotkey: z.string().min(1).max(AUDIT_VARCHAR_MAX).nullable(),
  /** Originating input surface — the `commandSourceSchema` enum. */
  commandSource: commandSourceSchema,
  /**
   * Active space id at emit time (`model` / `layout-<n>`). Backed by a
   * `varchar(255)` column → bounded.
   */
  spaceContext: z.string().min(1).max(AUDIT_VARCHAR_MAX),
  /**
   * Ids of the entities the command touched (may be empty). Bounded to
   * `AUDIT_ENTITY_IDS_MAX` so a single large-selection command cannot inflate the
   * batch body past the request-size limit; the projection truncates a longer
   * list to fit (the record stays deliverable rather than being dropped whole).
   */
  entityIds: z.array(z.string().min(1)).max(AUDIT_ENTITY_IDS_MAX),
  /** Pre-command summary, when the emitter distinguishes it; `null` otherwise. */
  beforeSummary: z.string().min(1).nullable(),
  /** Post-command summary, when the emitter distinguishes it; `null` otherwise. */
  afterSummary: z.string().min(1).nullable(),
  status: auditPersistStatusSchema,
  /** Emit time (ms since epoch, UTC) → `occurred_at`. */
  occurredAtMs: z.number().int().nonnegative(),
});

/** Wire shape for a batched audit flush to `POST /api/v1/audit`. */
export const auditBatchSchema = z.object({
  schemaVersion: z.literal(AUDIT_SCHEMA_VERSION),
  events: z.array(auditRecordSchema).min(1).max(500),
});

export type AuditPersistStatus = z.infer<typeof auditPersistStatusSchema>;
export type AuditRecord = z.infer<typeof auditRecordSchema>;
export type AuditBatch = z.infer<typeof auditBatchSchema>;
